import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
	BehaviorSubject,
	Observable,
	of,
	throwError,
	iif,
	forkJoin
} from 'rxjs';
import { catchError, concatMap, tap, map } from 'rxjs/operators';
import { ComponentRoutesEnum } from '../../utils/component-routes';
import {
	BusinessTypeEnum,
	OtherCountry,
	PartyApiService,
	PartyTypeEnum
} from '@shared/services/party-api';
import { InitialOnboardingPartiesStateService } from './initial-state';
import {
	PartyWithRelationship,
	PartyWithRelationshipOrNull,
	SBBOnboardingParties
} from './models';
import { HttpErrorCodes } from '@shared/enums/error-codes.enum';
import { PartyOnboardingApiService } from './../party-onboarding-api/party-onboarding-api.service';
import { ErrorHandlerService } from '@shared/services/error-handler/error-handler.service';
import { AddressDetail } from '@shared/models/address-details.model';
import {
	FederalProvinceCodeEnum,
	QuebecProvinceCodeEnum
} from '@shared/enums/province-code.enum';
import { AccountIntentEnum } from '@shared/enums/account-intent.enum';
import { ContactDetail, EmploymentDetail, RetailParty } from '@shared/models/index';
import { PatchPartyOnboardingRequest } from '@shared/models/api.model';
import { PartyRelationshipTypeEnum } from '@shared/enums/party-relationship-type.enum';
import { LoaderService } from '../loader/loader.service';
import { AuthService } from '../auth-service/auth.service';
import { BusinessRegistryEnum } from '@shared/enums/business-registry.enum';
import { UtilityService } from '@shared/utils/utility.service';
import { Login } from '@shared/models/login.model';
import { EntityTypeEnum } from '@shared/enums/entity-type.enum';
import { TinStateEnum } from '@shared/enums/tin-state.enum';
import { FeatureFlagService } from '../feature-flag-service/feature-flag-service';

@Injectable({
	providedIn: 'root'
})
export class OnboardingFlowService {
	private partiesBehaviorSubject: BehaviorSubject<SBBOnboardingParties>;
	private partiesObservable: Observable<SBBOnboardingParties>;
	private parties: SBBOnboardingParties;
	private executionIdSubject: BehaviorSubject<string | null>;
	private executionIdObservable: Observable<string | null>;
	public isRegisteredSoleProp: boolean;
	public hasBusinessAddressChanged: boolean;
	public hasBusinessDetailsFlagChecked: boolean;
	public operateUnderLegalName: boolean;
	public tiesCountries: OtherCountry[];
	public isUsPerson: boolean;
	// Set the party to null if it's deleted to maintain indexes in array
	public shareholdersBehaviorSubject: BehaviorSubject<
		PartyWithRelationshipOrNull[]
	> = new BehaviorSubject([] as PartyWithRelationshipOrNull[]);

	// Used to pre-populate option on OB05 screen as we are no longer storing the crsFlag value (refer to DB2023-11871)
	public overseasTaxpayerValue: boolean;

	// Used to pre-populate checkbox on OB10a for Incorporated user that confirms the shareholders information
	public shareholdersConfirmation = false;

	public flowComplete = false;

	public getPartiesObservable() {
		return this.partiesObservable;
	}

	constructor(
		private router: Router,
		private partyService: PartyApiService,
		private initialStateService: InitialOnboardingPartiesStateService,
		private errorHandlerService: ErrorHandlerService,
		private partyOnboardingApiService: PartyOnboardingApiService,
		private loaderService: LoaderService,
		private authService: AuthService,
		private utilityService: UtilityService,
		private featureFlagService: FeatureFlagService
	) {
		this.partiesBehaviorSubject = new BehaviorSubject(
			this.initialStateService.initialState
		);
		this.partiesObservable = this.partiesBehaviorSubject.asObservable();
		this.partiesBehaviorSubject.subscribe((parties) => {
			this.parties = parties;
		});
		this.executionIdSubject = new BehaviorSubject<string | null>(null);
		this.executionIdObservable = this.executionIdSubject.asObservable();
	}

	public calculateTotalOwnership(): number {
		const userOwnership =
			this.parties.primaryIndividual.partyOnboardingRelationship
				.shareholderPercentage;
		let totalOwnership = 0;
		this.parties.additionalShareholders.forEach(
			(shareholder: PartyWithRelationshipOrNull) => {
				if (shareholder?.partyOnboardingRelationship.shareholderPercentage) {
					totalOwnership +=
						shareholder.partyOnboardingRelationship.shareholderPercentage;
				}
			}
		);
		if (userOwnership) {
			totalOwnership += userOwnership;
		}
		return totalOwnership;
	}

	private addAdditionalShareholder(party: PartyWithRelationship) {
		this.partiesBehaviorSubject.next({
			...this.parties,
			additionalShareholders: [...this.parties.additionalShareholders, party]
		});
	}

	private editShareholder(party: PartyWithRelationship, index: number) {
		const newShareholders = this.parties.additionalShareholders;
		newShareholders[index] = party;
		this.partiesBehaviorSubject.next({
			...this.parties,
			additionalShareholders: [...newShareholders]
		});
	}

	public removeAdditionalShareholder(
		selectedShareholder: PartyWithRelationship
	) {
		const newShareholders = this.parties.additionalShareholders.filter(
			(acc) => acc !== selectedShareholder
		);
		this.partiesBehaviorSubject.next({
			...this.parties,
			additionalShareholders: [...newShareholders]
		});
	}

	public getAdditionalShareholder(i: number): PartyWithRelationshipOrNull {
		return this.parties.additionalShareholders[i];
	}

	/**
	 * @param i - index to target
	 * @param updatedParty - the full party object
	 *
	 * use `getAdditionalShareholder` to get the existing data for the shareholder you need to update,
	 * and pass the already updated object to this function.
	 */
	public updateAdditionalShareholder(
		i: number,
		updatedParty: PartyWithRelationship
	) {
		if (this.parties.additionalShareholders[i] === null) return;
		this.partiesBehaviorSubject.next({
			...this.parties,
			additionalShareholders: [
				...this.parties.additionalShareholders.slice(0, i),
				updatedParty,
				...this.parties.additionalShareholders.slice(i + 1)
			]
		});
	}

	private genericError(): void {
		this.loaderService.hide();
		return this.errorHandlerService.goToErrorPage(
			ComponentRoutesEnum.GENERIC_ERROR_PAGE
		);
	}

	public setExecutionId(executionId: string): void {
		this.executionIdSubject.next(executionId);
	}

	public getExecutionIdObservable(): Observable<string | null> {
		return this.executionIdObservable;
	}

	private getExecutionId(): string | null {
		return this.executionIdSubject.getValue();
	}

	public marketingSubmit(email: string): void {
		this.loaderService.show();
		if(this.featureFlagService.getFlag("sbbHandBreak")) {
			this.loaderService.hide();
			this.router.navigate([
				ComponentRoutesEnum.HAND_BREAK
			]);
		} else {
			this.partyService
			.postParty({ 
				...this.parties.business,
				email,
				emailVerified: false,
				dupEmailCheck: true
			})
			.pipe(catchError((err) => throwError(() => err)))
			.subscribe({
				next: (res) => {
					this.partiesBehaviorSubject.next({
						...this.parties,
						business: {
							...this.parties.business,
							email,
							partyId: res.partyId
						}
					});
					this.loaderService.hide();
					this.errorHandlerService.goToErrorPage(
						ComponentRoutesEnum.NEW_SBB_USER_ERROR
					);
				},
				error: (err) => {
					this.loaderService.hide();
					if (
						err.error?.error?.code ===
						HttpErrorCodes.CUSTOMER_WITH_EMAIL_ALREADY_EXISTS
					) {
						this.partiesBehaviorSubject.next({
							...this.parties,
							business: {
								...this.parties.business,
								email
							}
						});
						this.router.navigate(
							['/' + ComponentRoutesEnum.LOGIN],
							{
								queryParams: { app: 'SBB', email }
							}
						);
					} else if (
						err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST
					) {
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.SOMETHING_WENT_WRONG
						);
					} else {
						this.errorHandlerService.genericError();
					}
				}
			});
		}
	}

	public emailOtpSubmit(pin: string): Observable<boolean> {
		if (!this.parties.business.partyId) {
			this.errorHandlerService.genericError();
			return of(false);
		}

		this.loaderService.show();
		return this.partyService
			.verifyOtp(
				pin,
				this.parties.business.email,
				this.parties.business.partyId
			)
			.pipe(
				concatMap(() =>
					this.partyOnboardingApiService
						.postPartyOnboardingExecution({
							// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
							partyId: this.parties.business.partyId!,
							partyType: PartyTypeEnum.BUSINESS
						})
						.pipe(
							concatMap((partyOnboardingResponse) => {
								this.setExecutionId(partyOnboardingResponse.executionId);
								return this.partyService
									.postParty({
										email: this.parties.business.email,
										partyType: PartyTypeEnum.INDIVIDUAL
									})
									.pipe(
										concatMap((partyResponse) => {
											this.partiesBehaviorSubject.next({
												...this.parties,
												primaryIndividual: {
													...this.parties.primaryIndividual,
													partyId: partyResponse.partyId
												}
											});
											const updatedOnboardingExecutionData = {
												executionId: this.getExecutionId() as string,
												partyRelationship: {
													partyId: partyResponse.partyId,
													partyType: PartyTypeEnum.INDIVIDUAL
												}
											};

											return this.partyOnboardingApiService
												.patchPartyOnboardingExecution(
													updatedOnboardingExecutionData
												)
												.pipe(
													map(() => {
														this.loaderService.hide();
														this.router.navigate([
															ComponentRoutesEnum.BUSINESS_TYPE
														]);
														return false;
													}),
													catchError(() => {
														this.errorHandlerService.genericError();
														return of(false);
													})
												);
										}),
										catchError(() => {
											this.errorHandlerService.genericError();
											return of(false);
										})
									);
							}),
							catchError((err) => {
								this.errorHandlerService.genericError();
								return throwError(() => err);
							})
						)
				),
				catchError((err) => {
					this.loaderService.hide();
					if (err.error?.error?.code === HttpErrorCodes.INCORRECT_EMAIL_PIN) {
						return of(true);
					} else if (
						err.error?.error?.code === HttpErrorCodes.CUSTOMER_OUTOFBAND_OTP
					) {
						this.errorHandlerService.genericError();
						return throwError(() => err);
					} else if (
						err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST
					) {
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.SOMETHING_WENT_WRONG
						);
						return throwError(() => err);
					} else {
						this.errorHandlerService.genericError();
						return throwError(() => err);
					}
				})
			);
	}

	public resendEmailOtp(email: string): void {
		if (!this.parties.business.partyId) {
			this.errorHandlerService.genericError();
		} else {
			this.loaderService.show();
			this.partyService
				.requestOtp(email, this.parties.business.partyId)
				.subscribe({
					next: () => this.loaderService.hide(),
					error: (err) => {
						if (err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
							this.errorHandlerService.goToErrorPage(
								ComponentRoutesEnum.SOMETHING_WENT_WRONG
							);
						}
						this.genericError();
					}
				});
		}
	}

	public businessTypeSubmit(
		businessType: BusinessTypeEnum,
		registeredSoleProp: boolean,
		operateUnderLegalName: boolean,
		legalName: string,
		operatingName?: string,
		financialInstitutionFlag?: boolean,
		strataCondominiumFlag?: boolean,
		usPerson?: boolean,
		passiveNonFinancialFlag?: boolean,
		payTaxesOnlyInCanada?: boolean
	): void {
		if (!this.parties.business.partyId) {
			this.genericError();
			return;
		} else if (
			businessType === BusinessTypeEnum.SOLE_PROPRIETORSHIP &&
			!registeredSoleProp &&
			!operateUnderLegalName
		) {
			this.genericError();
			return;
		}
		this.loaderService.show();
		if (businessType === BusinessTypeEnum.SOLE_PROPRIETORSHIP) {
			this.isRegisteredSoleProp = registeredSoleProp;
		} else {
			this.isRegisteredSoleProp = false;
		}
		this.operateUnderLegalName = operateUnderLegalName;
		if (operateUnderLegalName) {
			if (
				this.parties.primaryIndividual.existingCustomerLoginResponse?.data
					.customerDetails.customerFirstName &&
				this.parties.primaryIndividual.existingCustomerLoginResponse?.data
					.customerDetails.customerLastName
			) {
				legalName =
					this.parties.primaryIndividual.existingCustomerLoginResponse?.data
						.customerDetails.customerFirstName +
					' ' +
					this.parties.primaryIndividual.existingCustomerLoginResponse?.data
						.customerDetails.customerLastName;
			}
		}
		const businessPartyUpdates =
			businessType === BusinessTypeEnum.CORPORATION
				? {
					businessType,
					legalName,
					operatingName,
					financialInstitutionFlag,
					strataCondominiumFlag,
					passiveNonFinancialFlag
				}
				: {
					businessType,
					legalName,
					operatingName
				};
		if (financialInstitutionFlag === true) {
			this.partiesBehaviorSubject.next({
				...this.parties,
				business: {
					...this.parties.business,
					businessParty: {
						...this.parties.business.businessParty,
						...businessPartyUpdates
					}
				}
			});
			this.genericError();
			return;
		}
		if (passiveNonFinancialFlag && !payTaxesOnlyInCanada) {
			this.partiesBehaviorSubject.next({
				...this.parties,
				business: {
					...this.parties.business,
					businessParty: {
						...this.parties.business.businessParty,
						...businessPartyUpdates
					}
				}
			});
			this.genericError();
			return;
		}
		this.partyService
			.patchParty(
				{
					businessParty: businessPartyUpdates
				},
				this.parties.business.partyId
			)
			.pipe(
				concatMap(() => {
					this.partiesBehaviorSubject.next({
						...this.parties,
						business: {
							...this.parties.business,
							businessParty: {
								...this.parties.business.businessParty,
								...businessPartyUpdates
							}
						}
					});
					if (usPerson) {
						this.isUsPerson = usPerson;
					} else if (this.isUsPerson && !usPerson) {
						this.isUsPerson = false;
					}
					const relationshipType =
						businessType === BusinessTypeEnum.SOLE_PROPRIETORSHIP
							? PartyRelationshipTypeEnum.OWNER
							: PartyRelationshipTypeEnum.SIGNING_OFFICER;

					const updatedOnboardingExecutionData = {
						executionId: this.getExecutionId() as string,
						partyRelationship: {
							partyId: this.parties.primaryIndividual.partyId,
							partyType: PartyTypeEnum.INDIVIDUAL,
							relationship: relationshipType
						}
					};

					return this.partyOnboardingApiService.patchPartyOnboardingExecution(
						updatedOnboardingExecutionData
					);
				})
			)
			.subscribe({
				next: () => {
					this.loaderService.hide();
					this.router.navigate([
						ComponentRoutesEnum.BUSINESS_ACTIVITY
					]);
				},
				error: (err) => {
					if (err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.SOMETHING_WENT_WRONG
						);
					}
					this.genericError();
				}
			});
	}

	public incomeDetailsSubmit(
		recentAnnualRevenue: number,
		accountIntent: AccountIntentEnum,
	): void {
		if (!this.parties.business.partyId) {
			this.genericError();
			return;
		}

		this.loaderService.show();
		const businessPartyUpdates = {
			recentAnnualRevenue,
		};
		this.partyService
			.patchParty(
				{
					accountIntent,
					businessParty: businessPartyUpdates
				},
				this.parties.business.partyId
			)
			.subscribe({
				next: () => {
					this.partiesBehaviorSubject.next({
						...this.parties,
						business: {
							...this.parties.business,
							accountIntent,
							businessParty: {
								...this.parties.business.businessParty,
								...businessPartyUpdates
							}
						}
					});
					this.loaderService.hide();
					this.router.navigate([
						ComponentRoutesEnum.TAX_DETAILS
					]);
				},
				error: (err) => {
					if (err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.SOMETHING_WENT_WRONG
						);
					}
					this.genericError();
				}
			});
	}

	public businessActivitySubmit(
		industry: string,
		overseasTaxpayer: boolean,
		businessJurisdictionFlag: boolean,
		businessEntityType: EntityTypeEnum,
		otherCountries?: OtherCountry[]
	): void {
		if (!this.parties.business.partyId) {
			this.genericError();
			return;
		}

		if (otherCountries) {
			this.tiesCountries = otherCountries;
		}

		this.loaderService.show();
		const partyUpdates = {
			otherCountries,
			businessParty: {
				industry,
				businessJurisdictionFlag,
				...(businessEntityType !== (undefined || null) && {
					businessEntityType
				})
			}
		};
		const partyUpdatesWithoutTies = {
			businessParty: {
				industry,
				businessJurisdictionFlag,
				...(businessEntityType !== (undefined || null) && {
					businessEntityType
				})
			}
		};

		if(!overseasTaxpayer && this.parties.business?.otherCountries 
			&& this.parties.business?.otherCountries?.length > 0) {
			const updatedCountryList: any[] = [];
			if(otherCountries){
				const otherCountriesList = this.parties.business?.otherCountries.filter((country) => {
					return otherCountries.some((item) => country.countryCode === item.countryCode);
				});
				otherCountriesList.forEach((item) => {
					updatedCountryList.push({
						country: item.country,
						countryCode: item.countryCode,
						tinState: TinStateEnum.NONE
					});
				});
				
				
			} else {
				updatedCountryList.push(
					{
						country: 'Canada',
						countryCode: 'CA',
						tinState: TinStateEnum.NOT_APPLICABLE
					}
				);
			}
			partyUpdates.otherCountries =  updatedCountryList;
			this.partiesBehaviorSubject.next({
				...this.parties,
				business: {
					...this.parties.business,
					otherCountries: updatedCountryList	
				}
			});	
		}

		this.partyService
			.patchParty(partyUpdates, this.parties.business.partyId)
			.subscribe({
				next: () => {
					this.partiesBehaviorSubject.next({
						...this.parties,
						business: {
							...this.parties.business,
							...partyUpdatesWithoutTies,
							businessParty: {
								...this.parties.business.businessParty,
								...partyUpdatesWithoutTies.businessParty
							},
							retailParty: {
								...this.parties.business.retailParty
							}
						}
					});

					this.loaderService.hide();
					this.overseasTaxpayerValue = overseasTaxpayer;
					if (overseasTaxpayer) {
						this.router.navigate([
							ComponentRoutesEnum.FATCA
						]);
					} else {
						this.router.navigate([
							ComponentRoutesEnum.INCOME_DETAILS
						]);
					}
				},
				error: (err) => {
					if (err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.SOMETHING_WENT_WRONG
						);
					}
					this.genericError();
				}
			});
	}

	// updating the fatca tincountries when the user add/removes them and navigates back to the OB05

	public updateTinCountries(otherCountries: OtherCountry[]): void {
		let partyUpdates: SBBOnboardingParties;
		if (
			this.parties.business.businessParty?.businessType ===
			BusinessTypeEnum.SOLE_PROPRIETORSHIP
		) {
			partyUpdates = {
				...this.parties,
				primaryIndividual: {
					...this.parties.primaryIndividual,
					otherCountries
				}
			};
		} else {
			partyUpdates = {
				...this.parties,
				business: {
					...this.parties.business,
					otherCountries
				}
			};
		}
		this.partiesBehaviorSubject.next(partyUpdates);
	}

	public fatcaSubmit(
		otherCountries: OtherCountry[],
		hasSanctionedCountry: boolean
	): void {
		this.loaderService.show();
		let partyUpdates: SBBOnboardingParties;
		let partyId: string;

		if (hasSanctionedCountry) {
			this.genericError();
			return;
		}

		if (
			this.parties.business.businessParty?.businessType ===
			BusinessTypeEnum.SOLE_PROPRIETORSHIP
		) {
			partyUpdates = {
				...this.parties,
				primaryIndividual: {
					...this.parties.primaryIndividual,
					otherCountries
				}
			};

			if (this.parties.primaryIndividual.partyId) {
				partyId = this.parties.primaryIndividual.partyId;
			} else {
				this.genericError();
				return;
			}
		} else {
			partyUpdates = {
				...this.parties,
				business: {
					...this.parties.business,
					otherCountries
				}
			};
			if (this.parties.business.partyId) {
				partyId = this.parties.business.partyId;
			} else {
				this.genericError();
				return;
			}
		}

		this.partyService.patchParty({ otherCountries }, partyId).subscribe({
			next: () => {
				this.partiesBehaviorSubject.next(partyUpdates);
				this.loaderService.hide();
				this.router.navigate([
					ComponentRoutesEnum.INCOME_DETAILS
				]);
			},
			error: (err) => {
				if (err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
					this.errorHandlerService.goToErrorPage(
						ComponentRoutesEnum.SOMETHING_WENT_WRONG
					);
				}
				this.genericError();
			}
		});
	}

	public taxDetailsSubmit(
		businessNumber: string,
		businessRegistry: BusinessRegistryEnum,
		businessRegistrationNumber: string,
		incorporationYear: string | undefined,
		address: AddressDetail,
		mobileDetails?: ContactDetail,
		hasAddressChanged?: boolean,
		hasBusinessDetailsFlagChecked?: boolean
	) {
		if (!this.parties.business.partyId) {
			this.genericError();
			return;
		}

		if (
			address?.province.toString() === QuebecProvinceCodeEnum.QC ||
			businessRegistry?.toString() === QuebecProvinceCodeEnum.QC
		) {
			this.errorHandlerService.quebecRestriction();
			return;
		}

		this.hasBusinessAddressChanged = hasAddressChanged ?? false;
		this.hasBusinessDetailsFlagChecked = hasBusinessDetailsFlagChecked ?? false;

		let updatedPartyData: any = {
			businessParty: {
				bin: businessNumber
			},
			addressDetails: [address],
			contactDetails: mobileDetails ? [mobileDetails] : undefined
		};

		if (businessRegistry) {
			if (businessRegistry.toString() === FederalProvinceCodeEnum.FEDERAL) {
				updatedPartyData.businessParty.businessRegistry = 'CD';
			} else {
				updatedPartyData.businessParty.businessRegistry = businessRegistry;
			}
		}

		if (businessRegistrationNumber) {
			updatedPartyData.businessParty.businessRegistrationNumber =
				businessRegistrationNumber;
		}

		if (JSON.stringify(updatedPartyData.businessParty) === '{}') {
			updatedPartyData = { addressDetails: [address] };
		}
		if (incorporationYear) {
			updatedPartyData.businessParty.incorporationYear = incorporationYear;
			updatedPartyData.businessParty.incorporationCountry = 'CA';
		}
		this.partyService
			.patchParty(updatedPartyData, this.parties.business.partyId)
			.subscribe({
				next: () => {
					this.partiesBehaviorSubject.next({
						...this.parties,
						business: {
							...this.parties.business,
							...updatedPartyData,
							businessParty: {
								...this.parties.business.businessParty,
								...updatedPartyData.businessParty
							}
						}
					});
					this.loaderService.hide();
					if (
						this.parties.business.businessParty?.businessType ===
						BusinessTypeEnum.CORPORATION
					) {
						this.router.navigate([
							ComponentRoutesEnum.CONTACT_OWNERSHIP
						]);
						return;
					}
					if (
						this.parties.primaryIndividual.existingCustomerLoginResponse !==
						null
					) {
						this.router.navigate([
							ComponentRoutesEnum.REVIEW
						]);
						return;
					}
					this.router.navigate([
						ComponentRoutesEnum.CONTACT_DETAILS
					]);
				},
				error: (err) => {
					if (err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.SOMETHING_WENT_WRONG
						);
					}
					this.genericError();
				}
			});
	}

	public contactOwnershipSubmit(
		isDirector: boolean,
		occupation: string,
		shareholderOwnershipPercentage: number
	): void {
		if (!this.parties.primaryIndividual.partyId) {
			this.errorHandlerService.genericError();
			return;
		}
		if (!isDirector) {
			this.errorHandlerService.ownershipRestriction();
			return;
		}
		this.loaderService.show();
		const executionId = this.getExecutionId();
		const updatedOnboardingExecutionData = {
			executionId: executionId as string,
			partyRelationship: {
				partyId: this.parties.primaryIndividual.partyId,
				partyType: PartyTypeEnum.INDIVIDUAL,
				shareholderPercentage: shareholderOwnershipPercentage
			}
		};

		const employmentDetail: Partial<EmploymentDetail> = { occupation };
		const updatedRetailParty: Partial<RetailParty> = {
			employmentDetails: [employmentDetail as EmploymentDetail]
		};
		const updatePartyData = {
			...this.parties.primaryIndividual,
			retailParty: {
				...this.parties.primaryIndividual.retailParty,
				...(updatedRetailParty as RetailParty)
			},
			partyOnboardingRelationship: {
				...this.parties.primaryIndividual.partyOnboardingRelationship,
				shareholderPercentage: shareholderOwnershipPercentage,
				relationship: PartyRelationshipTypeEnum.DIRECTOR
			}
		};

		if (
			!(this.parties.primaryIndividual.existingCustomerLoginResponse !== null)
		) {
			this.partyService
				.patchParty(
					{
						retailParty: updatedRetailParty as RetailParty
					},
					this.parties.primaryIndividual.partyId
				)
				.subscribe({
					next: () => {
						this.partiesBehaviorSubject.next({
							...this.parties,
							primaryIndividual: updatePartyData
						});
						this.callPatchPartyOnboarding(updatedOnboardingExecutionData);
					},
					error: (err) => {
						if (err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
							this.errorHandlerService.goToErrorPage(
								ComponentRoutesEnum.SOMETHING_WENT_WRONG
							);
						}
						this.genericError();
					}
				});
		} else {
			this.partiesBehaviorSubject.next({
				...this.parties,
				primaryIndividual: updatePartyData
			});
			this.callPatchPartyOnboarding(updatedOnboardingExecutionData);
		}
	}

	private callPatchPartyOnboarding(data: PatchPartyOnboardingRequest): void {
		this.partyOnboardingApiService
			.patchPartyOnboardingExecution(data)
			.subscribe({
				next: () => {
					const nextStep =
						this.parties.primaryIndividual.existingCustomerLoginResponse !==
						null
							? ComponentRoutesEnum.OWNERSHIP_STRUCTURE
							: ComponentRoutesEnum.CONTACT_DETAILS;
					this.loaderService.hide();
					this.router.navigate([nextStep]);
				},
				error: (err) => {
					if (err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.SOMETHING_WENT_WRONG
						);
					}
					this.genericError();
				}
			});
	}

	public prospectKycSubmit(
		firstName: string,
		lastName: string,
		phoneNumber: string,
		dateOfBirth: string,
		occupation?: string,
		sin?: string,
		sinAgreement?: boolean,
		address?: AddressDetail
	) {
		if (!this.parties.primaryIndividual.partyId) {
			this.errorHandlerService.genericError();
			return;
		}
		this.loaderService.show();
		if (!address && this.parties.business.addressDetails) {
			// eslint-disable-next-line no-param-reassign
			address = this.parties.business.addressDetails[0];
		}

		if (address?.province.toString() === QuebecProvinceCodeEnum.QC) {
			this.errorHandlerService.quebecRestriction();
			return;
		}

		const updatedPartyData: any = {
			firstName,
			lastName,
			retailParty: {
				dateOfBirth,
				...(this.parties.business.businessParty?.businessType ===
					BusinessTypeEnum.CORPORATION && sin
					? { sin, sinConsent: sinAgreement }
					: undefined),
				...(this.parties.business.businessParty?.businessType ===
					BusinessTypeEnum.SOLE_PROPRIETORSHIP && {
					sin,
					sinConsent: sinAgreement
				})
			},
			contactDetails: [
				{
					mobileNumber: phoneNumber
				}
			],
			addressDetails: [address]
		};

		if (occupation) {
			updatedPartyData.retailParty.employmentDetails = [{ occupation }];
		}

		this.partyService
			.patchParty(updatedPartyData, this.parties.primaryIndividual.partyId)
			.subscribe({
				next: () => {
					this.partiesBehaviorSubject.next({
						...this.parties,
						primaryIndividual: {
							...this.parties.primaryIndividual,
							...updatedPartyData
						}
					});
					this.loaderService.hide();
					if (
						this.parties.business.businessParty?.businessType ===
						BusinessTypeEnum.CORPORATION
					) {
						this.router.navigate([
							ComponentRoutesEnum.OWNERSHIP_STRUCTURE
						]);
					} else {
						this.router.navigate([
							ComponentRoutesEnum.REVIEW
						]);
					}
				},
				error: (err) => {
					if (
						this.parties.business.businessParty?.businessType ===
							BusinessTypeEnum.CORPORATION &&
						err.error.error.code === HttpErrorCodes.AGE_OF_MAJORITY
					) {
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.AGE_RESTRICTION_DIRECTOR
						);
					} else if (
						err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST
					) {
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.SOMETHING_WENT_WRONG
						);
					} else {
						this.errorHandlerService.handleErrorResponse(err);
					}
				}
			});
	}

	public ownershipStructureAdd(): void {
		this.router.navigate([
			ComponentRoutesEnum.ADD_EDIT_DIRECTOR_SHAREHOLDER
		]);
		return;
	}

	public ownershipStructureEdit(shareholder: any): void {
		this.router.navigate(
			[
				ComponentRoutesEnum.ADD_EDIT_DIRECTOR_SHAREHOLDER
			],
			{
				queryParams: { partyId: shareholder.partyId, index: shareholder.index }
			}
		);
		return;
	}

	public ownershipStructureSubmit(): void {
		this.shareholdersConfirmation = true;
		this.router.navigate([
			ComponentRoutesEnum.REVIEW
		]);
		return;
	}

	public reviewSubmit(accountConsent: boolean, password: string): void {
		if (!this.parties.primaryIndividual.partyId) {
			this.genericError();
			return;
		}

		const hasShareholders = this.parties.additionalShareholders.some(
			(shareholder) => shareholder !== null
		);
		const isCorporation =
			this.parties.business.businessParty?.businessType ===
			BusinessTypeEnum.CORPORATION;

		if (hasShareholders && isCorporation) {
			this.processShareholders(accountConsent, password);
		} else {
			this.continueFlow(accountConsent, password).subscribe();
		}
	}

	private processShareholders(accountConsent: boolean, password: string): void {
		this.loaderService.show();
	
		const shareholderRequests$ = forkJoin(
			this.parties.additionalShareholders
				.filter(shareholder => shareholder !== null)
				.map(shareholder => {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					const shareholderParty = this.utilityService.getPartyFromPartyWithRelationship(shareholder!);
					const shareholderPartyRelationship = shareholder?.partyOnboardingRelationship;
	
					return this.partyService.postParty({ ...shareholderParty, emailVerified: false, dupEmailCheck: false }).pipe(
						concatMap((res) => {
							// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
							shareholderPartyRelationship!.partyId = res.partyId;
							const updatedOnboardingExecutionData = {
								executionId: this.getExecutionId() as string,
								// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
								partyRelationship: shareholderPartyRelationship!
							};
							const patchPartyIfNFE = iif(
								() => this.parties.business.businessParty?.passiveNonFinancialFlag === true,
								this.partyService.patchParty(
									{
										otherCountries: [
											{
												country: 'Canada',
												countryCode: 'CA',
												usPerson: false,
												crsFlag: false,
												tinState: TinStateEnum.NOT_APPLICABLE
											}
										]
									},
									res.partyId
								),
								of(null)
							);
							return patchPartyIfNFE.pipe(
								concatMap(() =>
									this.partyOnboardingApiService.patchPartyOnboardingExecution(
										updatedOnboardingExecutionData
									)
								),
								catchError((error) => {
									if (error.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
										this.errorHandlerService.goToErrorPage(
											ComponentRoutesEnum.SOMETHING_WENT_WRONG
										);
									}
									return throwError(() => error);
								})
							);
						}),
						catchError((error) => {
							if (error.error?.code === HttpErrorCodes.AGE_OF_MAJORITY) {
								this.errorHandlerService.goToErrorPage(
									ComponentRoutesEnum.AGE_RESTRICTION_DIRECTOR
								);
							}
							return throwError(() => error);
						})
					);
				})
		);
	
		shareholderRequests$
			.pipe(
				catchError(() => {
					this.genericError();
					return of(false);
				}),
				concatMap(() => this.continueFlow(accountConsent, password))
			)
			.subscribe();
	}
	


	private continueFlow(
		accountConsent: boolean,
		password: string
	): Observable<any> {
		const businessPartyUpdates = { accountConsent };

		const postPasswordIfNewCustomer$ = iif(
			() =>
				this.parties.primaryIndividual.existingCustomerLoginResponse !== null,
			of(null),
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			this.authService.postPassword(
				this.parties.primaryIndividual.partyId!,
				password
			)
		);

		return postPasswordIfNewCustomer$.pipe(
			concatMap(() =>
				this.partyService.patchParty(
					businessPartyUpdates,
					this.parties.business.partyId || ''
				)
			),
			catchError((error) => {
				switch (error.error?.code) {
					case HttpErrorCodes.PARTY_NOT_FOUND:
					case HttpErrorCodes.CREDENTIALS_CREATION_FAILURE:
					case HttpErrorCodes.INVALID_PASSWORD_CREATION:
						this.genericError();
						break;
					default:
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.SOMETHING_WENT_WRONG
						);
						break;
				}
				return throwError(() => error);
			}),
			tap(() => {
				this.partiesBehaviorSubject.next({
					...this.parties,
					business: {
						...this.parties.business,
						businessParty: {
							...this.parties.business.businessParty,
							...businessPartyUpdates
						}
					}
				});
				this.flowComplete = true;
				this.loaderService.hide();
				if (
					this.parties.primaryIndividual.retailParty?.sinConsent ||
					this.parties.primaryIndividual.existingCustomerLoginResponse !== null
				) {
					this.router.navigate([
						ComponentRoutesEnum.COMPLETE
					]);
				} else {
					this.router.navigate([
						ComponentRoutesEnum.VERIFICATION_GATED
					]);
				}
			}),
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			concatMap(() =>
				this.partyOnboardingApiService.submitPartyOnboardingExecution(
					this.getExecutionId()!
				)
			),
			catchError((error) => {
				if (error.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
					this.errorHandlerService.goToErrorPage(
						ComponentRoutesEnum.SOMETHING_WENT_WRONG
					);
				}
				this.errorHandlerService.genericError();
				return throwError(() => error);
			})
		);
	}

	public addEditShareholderDirectorSubmit(
		party: PartyWithRelationship,
		editIndex: number | null
	): void {
		this.loaderService.show();
		if (
			party.address &&
			party.address[0] &&
			party.address[0].province.toString() === BusinessRegistryEnum.QC
		) {
			this.errorHandlerService.quebecRestriction();
			return;
		}
		if (
			party.address &&
			party.address[0] &&
			(party.address[0].addressLine2 === '' ||
				party.address[0].addressLine2 === null)
		) {
			delete party.address[0].addressLine2;
		}
		if (
			party.address &&
			party.address[1] &&
			(party.address[1].addressLine2 === '' ||
				party.address[1].addressLine2 === null)
		) {
			delete party.address[1].addressLine2;
		}
		if (editIndex || editIndex === 0) {
			this.editShareholder(party, editIndex);
		} else {
			this.addAdditionalShareholder(party);
		}
		this.loaderService.hide();
		this.router.navigate([
			ComponentRoutesEnum.OWNERSHIP_STRUCTURE
		]);
	}

	public setContext(login: Login, username: string): void {
		this.loaderService.show();
		this.partiesBehaviorSubject.next({
			...this.parties,
			business: {
				...this.parties.business,
				email: username
			}
		});
		this.partyService
			.migrateT24Profile(this.parties.business.email, login.accessToken)
			.pipe(
				concatMap((resp) => {
					this.partiesBehaviorSubject.next({
						...this.parties,
						primaryIndividual: {
							...this.parties.primaryIndividual,
							firstName: login.data.customerDetails.customerFirstName,
							lastName: login.data.customerDetails.customerLastName,
							existingCustomerLoginResponse: login,
							partyId: resp.partyId
						}
					});
					return this.partyService.postParty(
						{
							email: this.parties.business.email,
							partyType: PartyTypeEnum.BUSINESS,
							emailVerified: true,
							dupEmailCheck: false
						},
						'EXISTINGRETAILUSER',
						this.parties.primaryIndividual.partyId
					);
				}),
				concatMap((postPartyRes) => {
					this.partiesBehaviorSubject.next({
						...this.parties,
						business: {
							...this.parties.business,
							partyId: postPartyRes.partyId
						}
					});
					return this.partyOnboardingApiService.postPartyOnboardingExecution({
						partyId: postPartyRes.partyId,
						partyType: PartyTypeEnum.BUSINESS
					});
				}),
				concatMap((postExecutionRes) => {
					this.setExecutionId(postExecutionRes.executionId);
					return this.partyOnboardingApiService.patchPartyOnboardingExecution({
						executionId: postExecutionRes.executionId,
						partyRelationship: {
							partyId: this.parties.primaryIndividual.partyId,
							partyType: PartyTypeEnum.INDIVIDUAL
						}
					});
				}),
				catchError((err: any) => {
					if (err.error?.error?.code === HttpErrorCodes.INVALID_REQUEST) {
						this.errorHandlerService.goToErrorPage(
							ComponentRoutesEnum.SOMETHING_WENT_WRONG
						);
						return throwError(() => err);
					}
					this.loaderService.hide();
					this.errorHandlerService.genericError();
					return throwError(() => err);
				})
			)
			.subscribe({
				next: () => {
					this.loaderService.hide();
					this.router.navigate([
						ComponentRoutesEnum.BUSINESS_TYPE
					]);
				},
				error: () => {
					this.loaderService.hide();
					this.errorHandlerService.genericError();
				}
			});
	}
}
